import { create } from 'zustand';
import type { IProdoqMenuResponse, IProdoqRoleResponse, ProdoqPermissionKey, ProdoqPermissionYn } from '../types/prodoq';

type ProdoqPermissionStore = {
  companyId: number | null;
  menus: IProdoqMenuResponse[];
  roles: IProdoqRoleResponse[];
  selectedMenuId: number | null;
  setPermissionData: (data: { companyId: number; menus: IProdoqMenuResponse[]; roles: IProdoqRoleResponse[] }) => void;
  resetPermissionData: () => void;
  setSelectedMenuId: (selectedMenuId: number | null) => void;
  updateMenuPermission: (menuId: number, roleId: number, permissionKey: ProdoqPermissionKey, checked: boolean) => void;
};

function toPermissionValue(checked: boolean): ProdoqPermissionYn {
  return checked ? 'Y' : 'N';
}

function createDefaultPermission(roleId: number) {
  return {
    roleId,
    read: 'N' as const,
    create: 'N' as const,
    update: 'N' as const,
    delete: 'N' as const,
  };
}

function updatePermissions(
  permissions: IProdoqMenuResponse['permissions'],
  roleId: number,
  permissionKey: ProdoqPermissionKey,
  checked: boolean,
) {
  const targetPermission = permissions.find((permission) => permission.roleId === roleId) ?? createDefaultPermission(roleId);

  const nextPermission = {
    ...targetPermission,
    [permissionKey]: toPermissionValue(checked),
  };

  if (permissionKey === 'read' && !checked) {
    nextPermission.create = 'N';
    nextPermission.update = 'N';
    nextPermission.delete = 'N';
  }

  if (permissionKey !== 'read' && checked) {
    nextPermission.read = 'Y';
  }

  const nextPermissions = permissions.map((permission) => {
    if (permission.roleId !== roleId) {
      return permission;
    }

    return nextPermission;
  });

  if (permissions.some((permission) => permission.roleId === roleId)) {
    return nextPermissions;
  }

  return [...nextPermissions, nextPermission];
}

export const prodoqPermissionStore = create<ProdoqPermissionStore>((set) => ({
  companyId: null,
  menus: [],
  roles: [],
  selectedMenuId: null,
  setPermissionData: ({ companyId, menus, roles }) => set({ companyId, menus, roles }),
  resetPermissionData: () => set({ companyId: null, menus: [], roles: [], selectedMenuId: null }),
  setSelectedMenuId: (selectedMenuId) => set({ selectedMenuId }),
  updateMenuPermission: (menuId, roleId, permissionKey, checked) =>
    set((state) => ({
      menus: state.menus.map((menu) => {
        if (menu.id === menuId) {
          return {
            ...menu,
            permissions: updatePermissions(menu.permissions, roleId, permissionKey, checked),
          };
        }

        if (!menu.children.length) {
          return menu;
        }

        return {
          ...menu,
          children: menu.children.map((child) => {
            if (child.id !== menuId) {
              return child;
            }

            return {
              ...child,
              permissions: updatePermissions(child.permissions, roleId, permissionKey, checked),
            };
          }),
        };
      }),
    })),
}));
